import React, { useState } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { useQuery, useMutation } from '@apollo/react-hooks';

import { ProjectCollapsible, ProjectData } from '../../components';
import {
  Text,
  Button,
  Icon,
  ActivityIndicator,
  showToast,
} from '../../core-ui';
import { SOFT_BLUE_VIOLET, CAPTION_TEXT } from '../../constants/colors';
import { FETCH_PROJECTS_QUERY } from '../../graphql/queries/projectList';
import { formatProject } from '../../helpers/convertProjectsFormat';
import {
  CREATE_PROJECT_MUTATION,
  DELETE_PROJECT_MUTATION,
  UPDATE_PROJECT_MUTATION,
  RESTORE_PROJECT_MUTATION,
} from '../../graphql/mutations/project';

import {
  DeleteProjectMutation,
  DeleteProjectMutationVariables,
} from '../../generated/DeleteProjectMutation';
import { FetchProjectQuery } from '../../generated/FetchProjectQuery';
import {
  CreateProjectMutation,
  CreateProjectMutationVariables,
} from '../../generated/CreateProjectMutation';
import {
  UpdateProjectMutation,
  UpdateProjectMutationVariables,
} from '../../generated/UpdateProjectMutation';
import {
  RestoreProjectMutation,
  RestoreProjectMutationVariables,
} from '../../generated/RestoreProjectMutation';

import { noProject } from '../../../assets';
let isNewProject = (p: ProjectData) => p.id === '-1';

let NEW_PROJECT: ProjectData = {
  id: '-1',
  name: 'New Project',
  clientEmail: '',
  clientPassword: '',
  googleSheetsURL: '',
};

export default function ProjectList() {
  let [errorText, setErrorText] = useState('');
  let [isDeleting, setDeleting] = useState(false); // Prevent deleting project when toast is still shown
  let [isAddingNew, setAddingNew] = useState(false);

  let { data: projectListRaw, loading: projectsLoading } = useQuery<
    FetchProjectQuery
  >(FETCH_PROJECTS_QUERY, {
    onError: (err) => setErrorText(err.message),
    pollInterval: 3000,
  });
  let projectList =
    projectListRaw && formatProject(projectListRaw.readAllProject);

  let [deleteProject, { loading: deleteLoading }] = useMutation<
    DeleteProjectMutation,
    DeleteProjectMutationVariables
  >(DELETE_PROJECT_MUTATION, {
    onError: (err) => setErrorText(err.message),
  });
  let [updateProject, { loading: updateLoading }] = useMutation<
    UpdateProjectMutation,
    UpdateProjectMutationVariables
  >(UPDATE_PROJECT_MUTATION, {
    onCompleted: () => {
      showToast({
        message: `Successfully updating project!`,
      });
    },
    onError: (err) => setErrorText(err.message),
    refetchQueries: [{ query: FETCH_PROJECTS_QUERY }],
  });
  let [createProject, { loading: createLoading }] = useMutation<
    CreateProjectMutation,
    CreateProjectMutationVariables
  >(CREATE_PROJECT_MUTATION, {
    onError: (err) => setErrorText(err.message),
    onCompleted: () => {
      showToast({
        message: `Successfully creating project!`,
      });
    },
    refetchQueries: [{ query: FETCH_PROJECTS_QUERY }],
  });
  let [restoreProject, { loading: restoreLoading }] = useMutation<
    RestoreProjectMutation,
    RestoreProjectMutationVariables
  >(RESTORE_PROJECT_MUTATION, {
    onError: (err) => setErrorText(err.message),
    refetchQueries: [{ query: FETCH_PROJECTS_QUERY }],
  });

  let addNewProject = () => {
    setAddingNew(true);
  };

  let onSaveChanges = (project: ProjectData) => {
    let { id, name, googleSheetsURL, clientEmail, clientPassword } = project;

    if (isNewProject(project)) {
      createProject({
        variables: {
          name,
          url: googleSheetsURL,
          clientEmail,
          clientPassword,
        },
      }).then(() => {
        setAddingNew(false);
      });
    } else {
      updateProject({
        variables: {
          idProject: id,
          name,
          url: googleSheetsURL,
          clientEmail,
          clientPassword,
        },
      });
    }
  };

  let onDeleteProject = (projectData: ProjectData) => {
    let { id, name } = projectData;
    setDeleting(true);

    deleteProject({
      variables: {
        id,
      },
    }).then(() => {
      showToast({
        message: `${name} successfully deleted.`,
        action: {
          label: 'UNDO',
          onPress: () => undoDeleteProject(projectData),
        },
        duration: 3500,
        onToastEnd: () => {
          setDeleting(false);
        },
      });
    });
  };

  let undoDeleteProject = (projectData: ProjectData) => {
    let { name } = projectData;
    restoreProject({
      variables: {
        id: projectData.id,
      },
    }).then(() => {
      showToast({
        message: `${name} successfully restored.`,
        duration: 3500,
      });
    });
  };

  if (
    projectsLoading ||
    createLoading ||
    deleteLoading ||
    updateLoading ||
    restoreLoading
  ) {
    return (
      <View style={[styles.container, styles.center]}>
        <ActivityIndicator />
      </View>
    );
  }

  // ***********************************
  // todo for next PR create error scene
  //************************************

  if (errorText) {
    showToast({
      message: errorText,
      duration: 2500,
    });
    setErrorText('');
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text weight="medium" style={styles.headerText}>
          Current Projects
        </Text>
        <Button
          buttonMode="invisible"
          title="Add Project"
          onPress={addNewProject}
          icon={() => (
            <Icon name="plus-circle-outline" color={SOFT_BLUE_VIOLET} />
          )}
          labelStyle={styles.addProjectText}
          style={styles.button}
        />
      </View>

      {!projectsLoading && projectList && projectList.length === 0 ? (
        <View style={{ alignItems: 'center' }}>
          <Image source={noProject} style={styles.noProject} />
          <Text type="header" style={styles.noProjectText}>
            {`Uh oh, we couldn't find anything.`}
          </Text>
          <Text color={CAPTION_TEXT} style={styles.captionText}>
            {`It seems that you don't have a project. Go add a new one!`}
          </Text>
        </View>
      ) : null}

      {isAddingNew && (
        <ProjectCollapsible
          projectData={NEW_PROJECT}
          onDeleteProject={() => setAddingNew(false)}
          onSaveChanges={onSaveChanges}
        />
      )}
      {projectList &&
        projectList.map((projectData: ProjectData, i: number) => (
          <ProjectCollapsible
            key={i}
            projectData={projectData}
            onDeleteProject={() => !isDeleting && onDeleteProject(projectData)}
            onSaveChanges={onSaveChanges}
          />
        ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    padding: 0,
    margin: 0,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerText: {
    fontSize: 18,
  },
  captionText: {
    fontSize: 16,
  },
  noProjectText: {
    fontSize: 22,
    marginBottom: 10,
  },
  noProject: {
    marginTop: 60,
    width: 250,
    height: 250,
  },
  addProjectText: {
    color: SOFT_BLUE_VIOLET,
    marginLeft: 12,
  },
});
